import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core'
import { DialogService } from 'primeng/dynamicdialog'
import { combineLatest, merge, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs'
import { debounceTime, filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators'
import { AttachmentComponent, EntityAttachment, EntityConfiguration } from '../../models/entity-configuration'
import { JmlTitleService } from '../../../../services/jml-title.service'
import { EntityConfigService } from '../../services/entity-config.service'
import { JmlUrlParamService } from '../../../../modules/navigation-state'
import { ActivatedRoute } from '@angular/router'
import { StoredEntityStateService } from '../../../../modules/entity-store-access'
import { EntityService } from '../../services/entity.service'

@Component({
  selector: 'jml-lib-entity-view',
  templateUrl: './entity-view.component.html',
  styleUrls: ['./entity-view.component.scss'],
  providers: [StoredEntityStateService]
})
export class EntityViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entryId: number // required for viewType 'singlePage' since ngOnChanges is called via change of entrId which triggers refresh
  @Input() configName: string
  @Input() layout: any // Predefined layout; If set route information will be ignored. Used for dashboards
  @Input() refreshTrigger: Subject<[string, number]>

  entryRepr: string
  excludedPanels: string[]
  configuration: EntityConfiguration
  hasContent: boolean
  allAttachments: EntityAttachment[]
  subscriptions: Subscription[] = []
  entitySubscription: Subscription

  headerComponent: AttachmentComponent
  headerData: any[]
  selectedEntity: any

  @Input() isRootEntity = true

  private _$entryIdInputValue: ReplaySubject<number> = new ReplaySubject<number>(1)
  private _entityType$: Observable<string>
  private _entityId$: Observable<number>

  constructor(
    public dialogService: DialogService,
    private titleService: JmlTitleService,
    private entityConfigService: EntityConfigService,
    private jmlUrlParamService: JmlUrlParamService,
    private route: ActivatedRoute,
    private storedEntityWrapper: StoredEntityStateService,
    private entityService: EntityService
  ) {}

  ngOnInit() {
    const routeData$ = this.route.data.pipe(
      filter((data) => data.entityType),
      map((data) => data.entityType)
    )

    if (!this.layout) {
      if (this.configName) {
        this._entityType$ = of(this.configName)
      } else {
        this._entityType$ = merge(
          routeData$,
          this.route.queryParams.pipe(
            debounceTime(100),
            // check only for query params, if no route data is provided.
            takeUntil(routeData$),
            filter((params) => params.type),
            map((params) => params.type)
          )
        )
      }
      this.subscriptions.push(
        this._entityType$
          .pipe(
            // tap(type => console.log('type changed: ', type)),
            switchMap((type) => this.entityConfigService.getDtoType$(type)),
            tap((dtoType) => {
              this.configuration = dtoType.config
              if (!this.configuration) return
              this.preProcessAttachments(this.configuration)
              this.excludedPanels = dtoType.excludedPanels
              this.headerData = dtoType.headerFields
              this.configName = this.configuration.type
              if (this.isRootEntity) {
                this.titleService.setAppTitle(dtoType.config.displayName)
              }
            })
          )
          .subscribe()
      )
    }

    // this takes either the id from the url or from the input-param (first input value deactivates url-param processing.)
    this._entityId$ = merge(
      this._$entryIdInputValue,
      this.jmlUrlParamService.readIdFromRouteParam$(this.route).pipe(
        takeUntil(this._$entryIdInputValue)
        // tap(id => console.log('param id changed: ', id)),
      )
    )

    this.init()
  }

  init() {
    const currentEntity$: Observable<object> = combineLatest([this._entityType$, this._entityId$]).pipe(
      filter(([, entityId]) => !!entityId),
      tap(([, entityId]) => (this.entryId = entityId)),
      // tap(([entityType, entityId]) => console.log('setting wrapper to: ', entityType, entityId)),
      tap(([entityType, entityId]) => this.storedEntityWrapper.setEntityWithTypeAndId(entityType, entityId)),
      switchMap(() => this.storedEntityWrapper.entity$),
      map((entity) => entity?.fields),
      tap((entity) => {
        this.selectedEntity = entity
        if (!!this.selectedEntity) {
          this.entryRepr = this.selectedEntity['_repr_']
        }
      })
    )

    if (!!this.entitySubscription) {
      this.entitySubscription.unsubscribe()
      this.entitySubscription = null
    }

    this.entitySubscription = currentEntity$.subscribe()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.layout) {
      this.preProcessAttachments(<EntityConfiguration>{ entityAttachments: this.layout })
      if (!changes.layout.firstChange) {
        this.init()
      }
      return
    }

    if (changes.entryId) {
      this._$entryIdInputValue.next(changes.entryId.currentValue)
      if (!changes.entryId.firstChange) {
        this.init()
      }
    }
    // console.log('ngOnChanges', changes);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
    this.entitySubscription.unsubscribe()
  }

  // Checks whether the attachment can dynamically adjust its height
  // hasFlexibleHeight(component: AttachmentComponent): boolean {
  //   switch (component.type) {
  //     case 'component':
  //       switch (component.name) {
  //         case 'QuickNotes':
  //           return true;
  //       }
  //       break;
  //     case 'dashlet':
  //       switch (component.dashlet?.type) {
  //         case 'lineChart':
  //         case 'log':
  //           return true;
  //       }
  //       break;
  //   }

  //   return false;
  // }

  preProcessAttachments(config: EntityConfiguration) {
    const attachments = config.entityAttachments
    let rowWidth = 0
    let row: EntityAttachment[] = []
    this.allAttachments = []

    this.hasContent = !!config?.entityAttachments

    if (this.hasContent) {
      attachments.forEach((attachment) => {
        const attachmentWidth = Number.parseInt(attachment.width)
        if (rowWidth + attachmentWidth > 100) {
          this.allAttachments.push(<EntityAttachment>(<any>row))
          row = []
          rowWidth = 0
        }
        row.push(attachment)
        rowWidth += attachmentWidth
      })
    }

    if (row.length > 0) {
      this.allAttachments.push(<EntityAttachment>(<any>row))
    }
  }

  // cache for evlaluated conditions
  private conditionCache = new Map<string, Observable<boolean>>()

  getElementVisibility$(element: AttachmentComponent): Observable<boolean> {
    if (!element.condition) {
      return of(true)
    }

    const key = `${element.condition}`

    if (!this.conditionCache.has(key)) {
      // create an "intelligent" observable that initially evaluates the condition & updates itself when the selected entity changes
      const entityChanges$ = this.storedEntityWrapper.entity$.pipe()

      // combine initial evaluation with the following changes:
      const state$ = entityChanges$.pipe(
        // for every new entity re-evaluate the condition
        switchMap(() => this.evaluateCondition$(element.condition)),
        // share the last result (1) with all subscribers (rxjs-operator)
        shareReplay(1)
      )

      this.conditionCache.set(key, state$)
      return state$
    }

    return this.conditionCache.get(key)
  }

  private evaluateCondition$(condition: string): Observable<boolean> {
    const entityReference = {
      dtoType: this.configName,
      id: this.entryId
    }

    return this.entityService
      .eval(condition.includes('${') ? condition : `\${${condition}}`, 'boolean', entityReference)
      .pipe(
        // convert result into boolean
        map((response) => !!response.result)
      )
  }
}
